import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  onSnapshot,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

export const chatService = {
  // Create a new conversation
  async createConversation(conversationData) {
    try {
      const docRef = await addDoc(collection(db, 'conversations'), {
        ...conversationData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessage: null,
        lastMessageTime: null,
        lastMessageSenderId: null,
        lastReadBy: {}
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Get conversation by ID
  async getConversation(conversationId) {
    try {
      const conversationDoc = await getDoc(doc(db, 'conversations', conversationId));
      if (conversationDoc.exists()) {
        return { id: conversationDoc.id, ...conversationDoc.data() };
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Update conversation
  async updateConversation(conversationId, updates) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        ...updates
        // Removed automatic updatedAt update to prevent unnecessary refreshes
        // updatedAt will only be updated when explicitly needed
      });
    } catch (error) {
      throw error;
    }
  },

  // Get conversations for a user
  async getConversationsByUser(userId) {
    try {
      console.log('getConversationsByUser called with userId:', userId);
      
      // Temporarily remove orderBy to avoid index requirement
      const q = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId)
        // orderBy('updatedAt', 'desc') // Temporarily commented out
      );
      console.log('Query created, executing...');
      
      const querySnapshot = await getDocs(q);
      console.log('Query executed, processing results...');
      
      const conversations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Processed conversations:', conversations.length);
      
      // Sort conversations client-side by lastMessageTime to prevent clicked chats from moving to top
      conversations.sort((a, b) => {
        const dateA = a.lastMessageTime?.toDate?.() || new Date(a.lastMessageTime || 0);
        const dateB = b.lastMessageTime?.toDate?.() || new Date(b.lastMessageTime || 0);
        return dateB - dateA; // Descending order
      });
      
      return conversations;
    } catch (error) {
      console.error('Error in getConversationsByUser:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  },

  // Get conversations by ticket ID
  async getConversationsByTicket(ticketId) {
    try {
      const q = query(
        collection(db, 'conversations'),
        where('ticketId', '==', ticketId)
      );
      const querySnapshot = await getDocs(q);
      const conversations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return conversations;
    } catch (error) {
      throw error;
    }
  },

  // Send a message
  async sendMessage(conversationId, messageData) {
    try {
      const messageRef = await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
        ...messageData,
        createdAt: serverTimestamp()
      });

      // Update conversation with last message info and mark sender as read
      await this.updateConversation(conversationId, {
        lastMessage: messageData.text,
        lastMessageTime: serverTimestamp(),
        lastMessageSenderId: messageData.senderId,
        [`lastReadBy.${messageData.senderId}`]: serverTimestamp(),
      });

      return messageRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Update user's last read timestamp for a conversation
  async updateLastRead(conversationId, userId) {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        [`lastReadBy.${userId}`]: serverTimestamp()
        // Removed updatedAt update to prevent unnecessary refreshes
      });
    } catch (error) {
      // Non-fatal: reading status should not break chat
      console.error('Failed to update last read timestamp:', error);
    }
  },

  // Get messages for a conversation
  async getMessages(conversationId) {
    try {
      const q = query(
        collection(db, 'conversations', conversationId, 'messages'),
        orderBy('createdAt', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return messages;
    } catch (error) {
      throw error;
    }
  },

  // Listen to conversation changes in real-time
  subscribeToConversation(conversationId, callback) {
    return onSnapshot(doc(db, 'conversations', conversationId), (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  },

  // Listen to conversations for a user
  subscribeToConversations(userId, callback) {
    try {
      // Avoid composite index requirement by omitting orderBy here.
      // We'll sort client-side by lastMessageTime to ensure conversations stay in order based on last message sent.
      const q = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', userId)
      );

      return onSnapshot(q, (querySnapshot) => {
        const conversations = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            // Sort by lastMessageTime instead of updatedAt to prevent clicked chats from moving to top
            const aDate = a.lastMessageTime?.toDate?.() || new Date(a.lastMessageTime || 0);
            const bDate = b.lastMessageTime?.toDate?.() || new Date(b.lastMessageTime || 0);
            return bDate - aDate;
          });
        callback(conversations);
      }, (error) => {
        console.error('Error in conversation subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up conversation subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Listen to messages in a conversation
  subscribeToMessages(conversationId, callback) {
    try {
      const q = query(
        collection(db, 'conversations', conversationId, 'messages'),
        orderBy('createdAt', 'asc')
      );

      return onSnapshot(q, (querySnapshot) => {
        const messages = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(messages);
      }, (error) => {
        console.error('Error in message subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error setting up message subscription:', error);
      callback([]);
      return () => {};
    }
  },

  // Create a direct message conversation between owner and tenant
  async createDirectMessage(ownerId, tenantId, propertyId, propertyName) {
    try {
      console.log('createDirectMessage called with:', { ownerId, tenantId, propertyId, propertyName });
      
      // Check if conversation already exists
      console.log('Checking for existing conversations...');
      const existingConversations = await this.getConversationsByUser(ownerId);
      console.log('Existing conversations found:', existingConversations.length);
      
      const existingConversation = existingConversations.find(conv => 
        conv.type === 'direct' && 
        conv.participants.includes(tenantId) &&
        conv.propertyId === propertyId
      );

      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation.id);
        return existingConversation.id;
      }

      // Create new conversation
      console.log('Creating new conversation...');
      const conversationData = {
        type: 'direct',
        participants: [ownerId, tenantId],
        propertyId: propertyId,
        propertyName: propertyName,
        title: `Chat - ${propertyName}`,
        createdBy: ownerId
      };
      console.log('Conversation data:', conversationData);
      
      const conversationId = await this.createConversation(conversationData);
      console.log('New conversation created with ID:', conversationId);

      return conversationId;
    } catch (error) {
      console.error('Error in createDirectMessage:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  },

  // Create a group chat for ticket resolution
  async createTicketGroupChat(ticketId, ticketTitle, ownerId, tenantId, serviceProviderIds) {
    try {
      // Check if conversation already exists
      const existingConversations = await this.getConversationsByTicket(ticketId);
      const existingConversation = existingConversations.find(conv => 
        conv.type === 'group' && conv.ticketId === ticketId
      );

      if (existingConversation) {
        return existingConversation.id;
      }

      // Create new group conversation
      const participants = [ownerId, tenantId, ...serviceProviderIds];
      const conversationId = await this.createConversation({
        type: 'group',
        participants: participants,
        ticketId: ticketId,
        ticketTitle: ticketTitle,
        title: `Ticket: ${ticketTitle}`,
        createdBy: ownerId
      });

      return conversationId;
    } catch (error) {
      throw error;
    }
  }
};
